import {
  ENTERED_FROM_SEARCH_BAR,
  ENTERED_FROM_RECOMMENDATIONS,
  ENTERED_FROM_SEARCH_PAGE,
  ENTERED_FROM_FBT,
  ENTERED_FROM_FBT_MODAL,
  CLICKED_PRODUCT_ID,
} from '../constants/tracking';

// All tracking flags that need to be cleared when setting a new source
const ALL_TRACKING_FLAGS = [
  ENTERED_FROM_SEARCH_BAR,
  ENTERED_FROM_RECOMMENDATIONS,
  ENTERED_FROM_SEARCH_PAGE,
  ENTERED_FROM_FBT,
  ENTERED_FROM_FBT_MODAL,
];

// Flag to prevent duplicate tracking
const DIRECT_TRACKING_FLAG = 'alfinder_direct_tracking_done';

// Keys for storing recommendation metadata
const RECOMMENDATION_METADATA_KEY = 'alfinder_recommendation_metadata';

/**
 * Clear all previous tracking flags to ensure only the most recent source is tracked
 */
export const clearAllTrackingFlags = (): void => {
  ALL_TRACKING_FLAGS.forEach((flag) => {
    sessionStorage.removeItem(flag);
  });
};

/**
 * Set tracking source and clear all other sources to track only the most recent interaction
 * @param source - The tracking constant to set as active
 * @param productId - The product ID that was clicked
 * @param metadata - Optional metadata (e.g., pageType, recommendationType for recommendations)
 */
export const setTrackingSource = (
  source: string,
  productId?: string,
  metadata?: { pageType?: string; recommendationType?: string },
): void => {
  // Clear all previous flags first
  clearAllTrackingFlags();

  // Set the new source
  sessionStorage.setItem(source, 'true');

  // Set product ID if provided
  if (productId) {
    sessionStorage.setItem(CLICKED_PRODUCT_ID, productId);
  }

  // Store metadata if provided (for recommendations)
  if (metadata) {
    sessionStorage.setItem(
      RECOMMENDATION_METADATA_KEY,
      JSON.stringify(metadata),
    );
  } else {
    // Clear metadata if not provided
    sessionStorage.removeItem(RECOMMENDATION_METADATA_KEY);
  }
};

/**
 * Get the current active tracking source
 * @returns The most recent tracking source or null if none found
 */
export const getCurrentTrackingSource = (): string | null => {
  // Check in priority order (most specific to least specific)
  if (sessionStorage.getItem(ENTERED_FROM_FBT_MODAL) === 'true') {
    return 'frequently_bought_together_modal';
  }
  if (sessionStorage.getItem(ENTERED_FROM_FBT) === 'true') {
    return 'frequently_bought_together';
  }
  if (sessionStorage.getItem(ENTERED_FROM_RECOMMENDATIONS) === 'true') {
    return 'recommendations';
  }
  if (sessionStorage.getItem(ENTERED_FROM_SEARCH_PAGE) === 'true') {
    return 'search_page';
  }
  if (sessionStorage.getItem(ENTERED_FROM_SEARCH_BAR) === 'true') {
    return 'search_bar';
  }

  return null; // No valid tracking source found
};

/**
 * Check if the current product matches the clicked product ID
 * @param currentProductId - The current product ID to check
 * @returns boolean indicating if this is the tracked product
 */
export const isTrackedProduct = (currentProductId: string): boolean => {
  const clickedProductId = sessionStorage.getItem(CLICKED_PRODUCT_ID);
  return clickedProductId === currentProductId;
};

/**
 * Check if we should track the current interaction
 * @param currentProductId - The current product ID to check
 * @returns boolean indicating if tracking should occur
 */
export const shouldTrack = (currentProductId: string): boolean => {
  const trackingSource = getCurrentTrackingSource();
  const isValidProduct = isTrackedProduct(currentProductId);

  // Only track if we have both a valid source and the correct product
  return trackingSource !== null && isValidProduct;
};

/**
 * Set flag to indicate that direct tracking has been done (to prevent ProductView duplicate)
 * This flag is automatically cleared after a short timeout
 */
export const setDirectTrackingFlag = (): void => {
  sessionStorage.setItem(DIRECT_TRACKING_FLAG, 'true');

  // Auto-clear the flag after 2 seconds to prevent it from persisting too long
  setTimeout(() => {
    sessionStorage.removeItem(DIRECT_TRACKING_FLAG);
  }, 2000);
};

/**
 * Check if direct tracking has already been done (to prevent ProductView duplicate)
 * @returns boolean indicating if direct tracking was recently performed
 */
export const hasDirectTrackingFlag = (): boolean => {
  return sessionStorage.getItem(DIRECT_TRACKING_FLAG) === 'true';
};

/**
 * Get stored recommendation metadata (pageType, recommendationType)
 * @returns metadata object or null if not found
 */
export const getRecommendationMetadata = (): {
  pageType?: string;
  recommendationType?: string;
} | null => {
  const metadata = sessionStorage.getItem(RECOMMENDATION_METADATA_KEY);
  if (!metadata) return null;

  try {
    return JSON.parse(metadata);
  } catch (error) {
    return null;
  }
};

/**
 * Clear stored recommendation metadata after it's been used
 */
export const clearRecommendationMetadata = (): void => {
  sessionStorage.removeItem(RECOMMENDATION_METADATA_KEY);
};
